import { Injectable, signal, untracked } from '@angular/core';
import { environment } from '../../../environments/environment';

/** Subsystem tags, so a consultation can be filtered by layer (§9.1). */
export type Subsystem =
  | 'usb'
  | 'audio'
  | 'jitsi'
  | 'control'
  | 'chat'
  | 'translation'
  | 'backend'
  | 'session';

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

export interface LogEvent {
  ts: string;
  level: LogLevel;
  subsystem: Subsystem;
  /** Stable identifier, e.g. 'mic.bound'. Greppable across web and native. */
  event: string;
  sessionId: string;
  data?: Record<string, unknown>;
}

const DEV_BUFFER_SIZE = 5_000;
const PROD_BUFFER_SIZE = 1_000;

/**
 * Structured logging (§9.1).
 *
 * Free-form `console.log('something happened', x)` is unfilterable and
 * uncorrelatable. Every entry here carries a subsystem, a stable event name, and
 * a `sessionId` shared with the native side, so one consultation is one
 * queryable timeline across both halves of the system rather than two streams
 * someone has to line up by hand.
 */
@Injectable({ providedIn: 'root' })
export class LoggerService {
  readonly events = signal<readonly LogEvent[]>([]);

  private sessionId = 'no-session';
  private readonly maxEvents = environment.production
    ? PROD_BUFFER_SIZE
    : DEV_BUFFER_SIZE;

  /** Correlates this session's web logs with the native side's. */
  setSessionId(sessionId: string): void {
    this.sessionId = sessionId;
  }

  getSessionId(): string {
    return this.sessionId;
  }

  debug(subsystem: Subsystem, event: string, data?: Record<string, unknown>): void {
    this.write('debug', subsystem, event, data);
  }

  /** Default level — `log` reads better than `info` at the call site. */
  log(subsystem: Subsystem, event: string, data?: Record<string, unknown>): void {
    this.write('info', subsystem, event, data);
  }

  warn(subsystem: Subsystem, event: string, data?: Record<string, unknown>): void {
    this.write('warn', subsystem, event, data);
  }

  error(subsystem: Subsystem, event: string, data?: Record<string, unknown>): void {
    this.write('error', subsystem, event, data);
  }

  clear(): void {
    this.events.set([]);
  }

  private write(
    level: LogLevel,
    subsystem: Subsystem,
    event: string,
    data?: Record<string, unknown>,
  ): void {
    const entry: LogEvent = {
      ts: new Date().toISOString(),
      level,
      subsystem,
      event,
      sessionId: this.sessionId,
      data,
    };

    this.appendToBuffer(entry);
    this.writeToConsole(entry);
  }

  private appendToBuffer(entry: LogEvent): void {
    // The buffer is retained in production too: when something fails, the
    // preceding events are the diagnosis, and they have to already exist.
    //
    // Written outside the reactive graph. Logging is a side effect that must
    // work from anywhere, including inside an `effect` — and Angular forbids
    // signal writes there by default, so a call from one used to throw NG0600
    // and take down the *caller*. That is precisely backwards for diagnostics:
    // the video tile's attach logging killed the effect that renders remote
    // video. `untracked` also stops readers of `events` from being treated as
    // depending on every log line.
    untracked(() => {
      const current = this.events();
      const next =
        current.length >= this.maxEvents
          ? [...current.slice(current.length - this.maxEvents + 1), entry]
          : [...current, entry];
      this.events.set(next);
    });
  }

  private writeToConsole(entry: LogEvent): void {
    // Production drops `debug` only. `info` carries the join sequence and the
    // remote-track events, which is the whole diagnosis for "the call came up
    // but I cannot see the other person" — and that is a fault that appears in
    // the deployed APK, over adb, or nowhere at all. Suppressing it in
    // production means the one place the logs are needed is the one place they
    // are absent (§9.2).
    if (environment.production && entry.level === 'debug') {
      return;
    }

    // The payload is folded into the message string rather than passed as a
    // second console argument. Android's WebView console bridge concatenates
    // arguments with `String()`, so every structured log arrived over adb as
    // `[audio] stetho.start.failed [object Object]` — the event name survived
    // and the diagnosis did not. Serialising here costs a JSON pass per line
    // and makes the payload readable in the one place it is actually read.
    const line = `[${entry.subsystem}] ${entry.event}${formatData(entry.data)}`;

    switch (entry.level) {
      case 'error':
        console.error(line);
        break;
      case 'warn':
        console.warn(line);
        break;
      default:
        console.log(line);
        break;
    }
  }
}

/** `' {"declaredRate":0}'`, or `''` when there is nothing to say. */
function formatData(data?: Record<string, unknown>): string {
  if (!data) return '';

  const keys = Object.keys(data);
  if (keys.length === 0) return '';

  const seen = new WeakSet<object>();

  try {
    const body = JSON.stringify(data, (_key, value: unknown) => {
      // An Error serialises to `{}` by default — name, message and stack are
      // all non-enumerable. Losing the message of a thrown error is exactly
      // the failure this whole function exists to prevent.
      if (value instanceof Error) {
        return { name: value.name, message: value.message, stack: value.stack };
      }

      if (typeof value === 'bigint') return `${value}n`;
      if (typeof value === 'function') return `[function ${value.name || 'anonymous'}]`;

      if (typeof value === 'object' && value !== null) {
        // A MediaStreamTrack or a Jitsi conference reaches the buffer intact
        // and may well point back at itself.
        if (seen.has(value)) return '[circular]';
        seen.add(value);
      }

      return value;
    });

    // `undefined` when the payload is a lone unserialisable value; keep the
    // event line rather than printing the string "undefined" after it.
    return body === undefined ? '' : ` ${body}`;
  } catch {
    // A throwing getter or `toJSON` must not take down the caller: a log line
    // is a side effect, and losing one is strictly better than losing the
    // consultation. Keep the event name — it is still the greppable half.
    return ' [unserialisable]';
  }
}
